import {
  createOcrCanvasVariants,
  createOcrSourceCanvas,
  type OcrSourceRegion,
} from '@/shared/camera/ocr-image-preprocess';
import { rankOcrCandidates, scoreOcrCandidate, splitOcrCandidateText } from '@/shared/camera/ocr-photo-candidate-text';
import { type OcrScannerResult, TesseractOcrScannerAdapter } from '@/shared/camera/tesseract-ocr-scanner-adapter';
import {
  createValueScanCandidate,
  VALUE_SCAN_CANDIDATE_KINDS,
  type ValueScanPurpose,
} from '@/shared/camera/value-scanner';

export type { OcrSourceRegion } from '@/shared/camera/ocr-image-preprocess';
export { dedupeOcrCandidates, splitOcrCandidateText } from '@/shared/camera/ocr-photo-candidate-text';

const OCR_TIMEOUT_MS = 30_000;
// Below this Tesseract is guessing (e.g. seven-segment LCD digits); showing nothing beats showing junk.
const MIN_TESSERACT_CONFIDENCE = 0.3;

export async function createOcrPhotoCandidates(file: Blob, purpose: ValueScanPurpose, region?: OcrSourceRegion) {
  const image = await loadImage(file);
  const source = createOcrSourceCanvas(image, region);
  const results = await withTimeout(
    scanCanvasVariants(source, purpose, region ? 'block' : 'sparse'),
    OCR_TIMEOUT_MS,
    'OCR did not finish in time. Try a sharper photo or enter the value manually.',
  );
  const candidates = results.flatMap(result =>
    splitOcrCandidateText(result.text, purpose).flatMap(rawValue => {
      const candidate = createValueScanCandidate({
        confidence: scoreOcrCandidate(result.confidence, rawValue, purpose),
        kind: VALUE_SCAN_CANDIDATE_KINDS.ocr,
        purpose,
        rawValue,
      });

      return candidate ? [candidate] : [];
    }),
  );

  return rankOcrCandidates(candidates, purpose);
}

async function scanCanvasVariants(
  source: HTMLCanvasElement,
  purpose: ValueScanPurpose,
  segmentation: 'block' | 'sparse',
) {
  const adapter = new TesseractOcrScannerAdapter();
  const results: OcrScannerResult[] = [];

  for (const variant of createOcrCanvasVariants(source)) {
    results.push(...(await adapter.scan(variant, { purpose, segmentation })));
  }

  return results.filter(result => result.confidence >= MIN_TESSERACT_CONFIDENCE);
}

function loadImage(file: Blob) {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new window.Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Unable to load photo for OCR.'));
    };
    image.src = url;
  });
}

async function withTimeout<T>(promise: Promise<T>, timeoutMs: number, message: string) {
  let timeoutId: number | undefined;

  try {
    return await Promise.race([
      promise,
      new Promise<never>((_, reject) => {
        timeoutId = window.setTimeout(() => reject(new Error(message)), timeoutMs);
      }),
    ]);
  } finally {
    if (timeoutId !== undefined) {
      window.clearTimeout(timeoutId);
    }
  }
}
